import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../hooks/usePayment';
import { CreditCard, AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';

interface PaymentProcessorProps {
  amount: number;
  onSuccess: () => void;
  onError: (error: string) => void;
  onCancel: () => void;
  autoStart?: boolean;
}

export const PaymentProcessor: React.FC<PaymentProcessorProps> = ({
  amount,
  onSuccess,
  onError,
  onCancel,
  autoStart = false,
}) => {
  const { t } = useTranslation();
  const { isProcessing, error, result, handleCardPayment } = usePayment();
  const [cancelMessage, setCancelMessage] = useState<string | null>(null);

  useEffect(() => {
    if (autoStart && !error && !result && !cancelMessage) {
      processPayment();
    }
  }, [autoStart, error, result, cancelMessage]);

  const processPayment = async () => {
    try {
      const paymentResult = await Promise.race([
        handleCardPayment(amount),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(t('Délai de paiement dépassé'))), 60000)
        ),
      ]);
      if (paymentResult.success) {
        onSuccess();
      } else {
        if (paymentResult.message === 'Paiement annulé par l\'utilisateur') {
          setCancelMessage(t('Paiement annulé'));
          setTimeout(() => {
            setCancelMessage(null);
            onCancel();
          }, 1000);
        } else {
          onError(
            paymentResult.message ||
              t('Paiement refusé. Veuillez vérifier votre carte ou contacter le personnel.')
          );
        }
      }
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : t('Erreur lors du paiement. Veuillez réessayer ou contacter le personnel.')
      );
    }
  };

  // Animation pour les messages
  const messageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg max-w-md w-full mx-auto border border-gray-100">
      {/* En-tête */}
      <div className="flex items-center gap-4 mb-6">
        <motion.div
          className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
        >
          <CreditCard className="w-6 h-6 text-orange-600" />
        </motion.div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">{t('Paiement par carte')}</h3>
          <p className="text-lg font-semibold text-orange-500">
            {t('Montant à payer', { price: amount.toFixed(2) })}
          </p>
        </div>
      </div>

      {/* Section des messages */}
      <motion.div
        key={cancelMessage || error || result?.success ? 'result' : 'processing'}
        variants={messageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="mb-6 text-center"
      >
        {cancelMessage && (
          <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-center gap-3 text-gray-600">
            <XCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{cancelMessage}</p>
          </div>
        )}
        {isProcessing && !cancelMessage && (
          <div className="p-4 bg-blue-50 rounded-lg flex items-center justify-center gap-3 text-blue-600">
            <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" />
            <p className="text-sm font-medium">{t('Traitement du paiement')}</p>
          </div>
        )}
        {error && !cancelMessage && (
          <div className="p-4 bg-red-50 rounded-lg flex items-center justify-center gap-3 text-red-600">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}
        {result?.success && !cancelMessage && (
          <div className="p-4 bg-green-50 rounded-lg flex items-center justify-center gap-3 text-green-600">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">{result.message}</p>
          </div>
        )}
      </motion.div>
    </div>
  );
};